import React from 'react';
import PropTypes from 'prop-types';
import { alpha, useTheme } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import FemaleIcon from '@mui/icons-material/Face2';
import WorkIcon from '@mui/icons-material/Work';
import HomeIcon from '@mui/icons-material/Home';
import SchoolIcon from '@mui/icons-material/School';
import SportsMartialArtsIcon from '@mui/icons-material/SportsMartialArts';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import LanguageIcon from '@material-ui/icons/Language';
import ShoppingBagIcon from '@material-ui/icons/ShoppingBag';

const DEFAULT_TITLE = 'The Modern Decision Maker';
const DEFAULT_INTRO =
  'Profesional universitario, bilingüe y con poder de decisión: consume contenido digital con intención y busca marcas que aporten valor real.';

const PERSONA_TRAITS = [
  'Sigue tendencias, eventos y lo que la mantiene actualizada.',
  'Valora la practicidad en cada interacción con una marca.',
  'Se informa y se entretiene casi exclusivamente por internet.',
  'Busca contenido que impulse su crecimiento personal y profesional.',
];

const PERSONA_FACTS = [
  {
    icon: FemaleIcon,
    label: 'Demografía',
    value: '35 años · Mujer · Clase media alta',
    accent: '#c2185b',
  },
  {
    icon: SchoolIcon,
    label: 'Formación',
    value: 'Profesional universitario · Bilingüe',
    accent: '#6a1b9a',
  },
  {
    icon: WorkIcon,
    label: 'Rol',
    value: 'Gerente de Marketing en empresa',
    accent: '#1565c0',
  },
  {
    icon: HomeIcon,
    label: 'Ubicación',
    value: 'Caracas, Venezuela',
    accent: '#00838f',
  },
  {
    icon: SportsMartialArtsIcon,
    label: 'Estilo de vida',
    value: 'Activa · se ejercita con regularidad',
    accent: '#2e7d32',
  },
  {
    icon: DirectionsCarIcon,
    label: 'Movilidad',
    value: 'Posee automóvil',
    accent: '#e65100',
  },
];

const DIGITAL_BEHAVIOR = [
  { icon: LanguageIcon, text: 'Streaming y On-Demand como rutina', accent: '#0277bd' },
  { icon: ShoppingBagIcon, text: 'Investiga antes de comprar o patrocinar', accent: '#5c6bc0' },
];

function PersonaCard({ children, accent, sx = {} }) {
  const theme = useTheme();
  const accentColor = accent || theme.palette.primary.main;
  return (
    <Box
      sx={{
        borderRadius: 3,
        p: { xs: 2, md: 2.25 },
        bgcolor: theme.palette.background.paper,
        border: `1px solid ${alpha(accentColor, 0.18)}`,
        boxShadow: `0 8px 24px ${alpha(accentColor, 0.08)}`,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

PersonaCard.propTypes = {
  children: PropTypes.node,
  accent: PropTypes.string,
  sx: PropTypes.object,
};

const BuyerPersona = ({ content = {} }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const secondary = theme.palette.secondary?.main || '#00838f';
  const personaName = content.title || DEFAULT_TITLE;
  const intro = content.description || DEFAULT_INTRO;

  return (
    <Box
      sx={{
        borderRadius: 4,
        overflow: 'hidden',
        background: `linear-gradient(165deg, ${alpha(primary, 0.05)} 0%, ${alpha(secondary, 0.08)} 48%, transparent 100%)`,
        px: { xs: 1, md: 2 },
        py: { xs: 3, md: 4 },
        my: { xs: 2, md: 3 },
      }}
    >
      <Box textAlign="center" mb={{ xs: 2.5, md: 3 }}>
        <Typography
          sx={{ textTransform: 'uppercase', fontWeight: 700, letterSpacing: 2.5, fontSize: '0.78rem' }}
          color="secondary"
          gutterBottom
        >
          Buyer persona
        </Typography>
        <Typography
          component="h2"
          sx={{
            fontWeight: 900,
            fontSize: { xs: '1.75rem', md: '2.1rem' },
            letterSpacing: '-0.02em',
            color: 'primary.main',
            mb: 1,
          }}
        >
          {personaName}
        </Typography>
        <Typography
          variant="body1"
          color="textSecondary"
          sx={{ maxWidth: 640, mx: 'auto', lineHeight: 1.65, fontSize: { xs: '0.95rem', md: '1.02rem' } }}
        >
          {intro}
        </Typography>
      </Box>

      <Grid container spacing={0} rowSpacing={2} columnSpacing={{ md: 2 }} alignItems="flex-start" sx={{ maxWidth: 1000, mx: 'auto' }}>
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              position: 'relative',
              border: `1px solid ${alpha(primary, 0.2)}`,
              boxShadow: `0 16px 40px ${alpha(primary, 0.12)}`,
            }}
          >
            <Box
              component="img"
              src="/images/buyer_persona.jpg"
              alt="Perfil tipo de audiencia TNO Network"
              sx={{
                display: 'block',
                width: '100%',
                height: { xs: 280, md: 'auto' },
                objectFit: 'cover',
                objectPosition: 'center top',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: `linear-gradient(180deg, transparent 45%, ${alpha('#0f172a', 0.85)} 100%)`,
                pointerEvents: 'none',
              }}
            />
            <Box sx={{ position: 'absolute', left: 16, right: 16, bottom: 16 }}>
              <Chip
                label="Núcleo de decisión · 35–44"
                size="small"
                sx={{
                  mb: 1,
                  bgcolor: alpha(secondary, 0.9),
                  color: '#fff',
                  fontWeight: 700,
                }}
              />
              <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1.05rem', lineHeight: 1.25 }}>
                Tomadora de decisiones digitales
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={8}>
          <PersonaCard accent={primary} sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', mb: 1.25 }}>Personalidad</Typography>
            <Grid container spacing={0} rowSpacing={1} columnSpacing={{ sm: 1 }}>
              {PERSONA_TRAITS.map((trait) => (
                <Grid item xs={12} sm={6} key={trait}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                      p: 1.25,
                      borderRadius: 2,
                      bgcolor: alpha(primary, 0.04),
                      border: `1px solid ${alpha(primary, 0.1)}`,
                      height: '100%',
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 18, color: primary, mt: 0.15, flexShrink: 0 }} />
                    <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.45, fontSize: '0.88rem' }}>
                      {trait}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </PersonaCard>

          <Grid container spacing={0} rowSpacing={1.25} columnSpacing={{ sm: 1.25 }} sx={{ mb: 2 }}>
            {PERSONA_FACTS.map((fact) => {
              const Icon = fact.icon;
              return (
                <Grid item xs={12} sm={6} key={fact.label}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      p: 1.25,
                      borderRadius: 2.5,
                      bgcolor: theme.palette.background.paper,
                      border: `1px solid ${alpha(fact.accent, 0.22)}`,
                      boxShadow: `0 4px 14px ${alpha(fact.accent, 0.06)}`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: alpha(fact.accent, 0.12),
                        color: fact.accent,
                        flexShrink: 0,
                      }}
                    >
                      <Icon fontSize="small" />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: fact.accent, display: 'block' }}>
                        {fact.label}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.35, fontSize: '0.85rem' }}>
                        {fact.value}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>

          <PersonaCard accent={secondary}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', mb: 1.25 }}>Comportamiento digital</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {DIGITAL_BEHAVIOR.map((item) => {
                const Icon = item.icon;
                return (
                  <Chip
                    key={item.text}
                    icon={<Icon sx={{ color: `${item.accent} !important` }} />}
                    label={item.text}
                    sx={{
                      fontWeight: 600,
                      py: 2,
                      height: 'auto',
                      '& .MuiChip-label': { whiteSpace: 'normal', lineHeight: 1.35 },
                      bgcolor: alpha(item.accent, 0.1),
                      color: item.accent,
                      border: `1px solid ${alpha(item.accent, 0.25)}`,
                    }}
                  />
                );
              })}
              <Chip
                label="Confía en recomendaciones editoriales, no solo en pauta"
                sx={{
                  fontWeight: 600,
                  py: 2,
                  height: 'auto',
                  maxWidth: '100%',
                  '& .MuiChip-label': { whiteSpace: 'normal', lineHeight: 1.35 },
                  bgcolor: alpha(primary, 0.08),
                  color: primary,
                  border: `1px solid ${alpha(primary, 0.2)}`,
                }}
              />
            </Box>
          </PersonaCard>
        </Grid>
      </Grid>
    </Box>
  );
};

BuyerPersona.propTypes = {
  themeMode: PropTypes.string,
  content: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
  }),
};

BuyerPersona.defaultProps = {
  content: {},
};

export default BuyerPersona;
